import { buildApiUrl } from './client';
import type { PdfDocType, PdfStudioBranding } from '../pdf/pdfStudioState';

export type PreviewDraftBody = {
  type: PdfDocType;
  branding?: PdfStudioBranding;
  pdfLayoutJson?: string;
  pdfLayoutJsonOverrides?: PdfStudioBranding['pdfLayoutJsonOverrides'];
};

/** Render preview HTML directly — avoids iframe X-Frame-Options blocks and multi-instance draft cache misses. */
export async function postPreviewHtml(body: PreviewDraftBody): Promise<string> {
  const res = await fetch(buildApiUrl('/api/pdf/preview?previewMode=studio'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify({ ...body, previewMode: 'studio' })
  });
  if (!res.ok) {
    const raw = await res.text().catch(() => '');
    let message = raw?.slice(0, 200) || `Preview failed (${res.status})`;
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed.error === 'string') message = parsed.error;
    } catch {
      /* use raw */
    }
    throw new Error(message);
  }
  return res.text();
}

/** @deprecated Draft + iframe URL — use postPreviewHtml instead */
export async function postPreviewDraft(body: PreviewDraftBody): Promise<string> {
  const res = await fetch(buildApiUrl('/api/pdf/preview/draft'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    cache: 'no-store',
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const raw = await res.text().catch(() => '');
    throw new Error(raw || `Preview draft failed (${res.status})`);
  }
  const data = (await res.json()) as { draftKey?: string };
  if (!data.draftKey) throw new Error('Preview draft key missing');
  return data.draftKey;
}

/** @deprecated Use postPreviewHtml + iframe srcDoc */
export function buildPreviewIframeSrc(type: PdfDocType, draftKey: string, rev: number): string {
  const qs = new URLSearchParams({
    type,
    draftKey,
    rev: String(rev),
    previewMode: 'studio'
  });
  return buildApiUrl(`/api/pdf/preview?${qs.toString()}`);
}
